import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { fetchFareQuote, fetchFareRules, FallbackAvailableError } from "../services/flightBookingAPI";
import BookingReviewError from "../components/bookingFlight/BookingReviewError";
import LoadingModal from "../components/modal/LoadingModal";
import FallbackErrorModal from "../components/modal/FallbackErrorModal";
import BookingReview from "../components/bookingFlight/BookingReview";
import TravellerDetails from "../components/bookingFlight/TravellerDetails";
import FareRules from "../components/bookingFlight/FareRules";
import FareDetails from "../components/bookingFlight/FareDetails";
import PaymentMethod from "../components/bookingFlight/PaymentMethod";
import BookingConfirmation from "../components/bookingFlight/BookingConfirmation";
import ContactSupportModal from "../components/bookingFlight/ContactSupportModal";
import { useAuth } from "../context/AuthContext";
import { getAirlineLogo } from "../utils/airlineImages";
import {
  RecaptchaVerifier
} from "firebase/auth";

const BookFlight = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract parameters from URL first, then fallback to location state
  const searchParams = new URLSearchParams(location.search);
  const trace_id = searchParams.get("trace_id") || location.state?.trace_id;
  const result_idx =
    searchParams.get("result_idx") || location.state?.result_idx;
  // eslint-disable-next-line no-unused-vars
  const is_lcc = searchParams.get("is_lcc") || location.state?.is_lcc; // Stored for potential future use
  // eslint-disable-next-line no-unused-vars
  const valid_till_ts = searchParams.get("valid_till_ts") || location.state?.valid_till_ts; // Stored for potential future use

  // Get userToken from localStorage or location state
  const [userToken, setUserToken] = useState(
    localStorage.getItem("userToken") ||
    location.state?.userToken ||
    ""
  );

  // API Data States
  const [fareQuoteData, setFareQuoteData] = useState(null);
  const [fareRulesData, setFareRulesData] = useState(null);

  // UI States
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const [currentPage, setCurrentPage] = useState("booking"); // 'booking', 'payment', or 'confirmation'
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isTransactionActive, setIsTransactionActive] = useState(false);

  const { user, userToken: globalToken, openLoginModal } = useAuth();

  useEffect(() => {
    if (globalToken) {
      setUserToken(globalToken);
    }
  }, [globalToken]);

  // Fallback Error Modal State
  const [fallbackError, setFallbackError] = useState(null);

  // User Input State - Pre-populate from Firebase Auth
  const [contactDetails, setContactDetails] = useState({
    email: "",
    phone: "",
    countryCode: "+91",
  });

  // Passenger data state for TBO API
  const [passengers, setPassengers] = useState([]);

  // Get user email/phone from Firebase Auth on mount
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      // console.log("[BookFlight] Firebase user found:", user.email, user.phoneNumber);
      user.getIdToken().then(token => {
        setUserToken(token);
      });
      setContactDetails(prev => ({
        ...prev,
        email: user.email || prev.email,
        phone: user.phoneNumber?.replace("+91", "") || prev.phone,
      }));
    } else {
      // console.log("[BookFlight] No Firebase user found, listening for auth state...");
      // Listen for auth state changes in case user isn't loaded yet
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          // console.log("[BookFlight] Firebase auth state changed:", user.email, user.phoneNumber);
          const token = await user.getIdToken();
          setUserToken(token);
          setContactDetails(prev => ({
            ...prev,
            email: user.email || prev.email,
            phone: user.phoneNumber?.replace("+91", "") || prev.phone,
          }));
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // Debug: Log contact details changes
  // useEffect(() => {
  //   console.log("BookFlight contactDetails updated:", contactDetails);
  // }, [contactDetails]);

  // Fetch booking data on mount
  useEffect(() => {
    // console.log("> useLocation; location: ", location.state);
    // console.log("> URL params:", {
    //   trace_id: searchParams.get("trace_id"),
    //   result_idx: searchParams.get("result_idx"),
    //   is_lcc: searchParams.get("is_lcc"),
    //   valid_till_ts: searchParams.get("valid_till_ts")
    // });

    const fetchBookingData = async () => {
      // Validate required parameters - if missing, show Contact Support Modal
      if (!trace_id || result_idx === undefined || result_idx === null) {
        console.error("Missing required booking parameters:", { trace_id, result_idx });
        setIsLoading(false);
        setIsContactModalOpen(true);
        return;
      }

      try {
        setIsLoading(true);
        setApiError(null);

        // Ensure we have a token (wait if necessary)
        let effectiveToken = userToken;
        if (!effectiveToken) {
          const user = auth.currentUser;
          if (user) {
            effectiveToken = await user.getIdToken();
            setUserToken(effectiveToken);
          }
        }

        if (!effectiveToken) {
          // If still no token, wait briefly for auth listener
          await new Promise(resolve => setTimeout(resolve, 1000));
          const user = auth.currentUser;
          if (user) {
            effectiveToken = await user.getIdToken();
            setUserToken(effectiveToken);
          }
        }

        if (!effectiveToken) {
          setApiError("Authentication required. Please login to continue.");
          setIsLoading(false);
          return;
        }

        // console.log("Fetching booking data with:", { trace_id, result_idx });

        // Fetch fareQuote first to get rc_booking_id
        const fareQuoteResponse = await fetchFareQuote(trace_id, result_idx, effectiveToken);

        // console.log("Fare Quote Response:", fareQuoteResponse);

        // Validate fareQuote response
        if (!fareQuoteResponse || fareQuoteResponse.status !== 200) {
          throw new Error(
            fareQuoteResponse?.message || "Failed to fetch fare quote"
          );
        }

        // Validate data structure
        if (!fareQuoteResponse.data?.Results) {
          throw new Error("Invalid fare quote data structure");
        }

        // Extract rc_booking_id from fareQuote response
        const rc_booking_id = fareQuoteResponse.rc_booking_id;
        // console.log("RC Booking ID from fareQuote:", rc_booking_id);

        // Now fetch fareRules with the rc_booking_id
        const fareRulesResponse = await fetchFareRules(trace_id, result_idx, userToken, rc_booking_id);

        // console.log("Fare Rules Response:", fareRulesResponse);

        // Validate fareRules response
        if (!fareRulesResponse || fareRulesResponse.status !== 200) {
          throw new Error(
            fareRulesResponse?.message || "Failed to fetch fare rules"
          );
        }

        if (!fareRulesResponse.data?.FareRules) {
          throw new Error("Invalid fare rules data structure");
        }

        // Success - store data
        setFareQuoteData(fareQuoteResponse.data);
        // Include rc_booking_id from the response root level in the fareRulesData
        setFareRulesData({
          ...fareRulesResponse.data,
          rc_booking_id: fareRulesResponse.rc_booking_id || rc_booking_id
        });
        setIsLoading(false);

        // console.log("✅ Booking data loaded successfully");
        // console.log("RC Booking ID:", rc_booking_id);
      } catch (error) {
        console.error("Error fetching booking data:", error);

        // Check if this is a fallback-available error
        if (error instanceof FallbackAvailableError) {
          // console.log("[BookFlight] Fallback data available, showing modal");
          setFallbackError(error);
          setIsLoading(false);
        } else {
          setApiError(
            error.message || "Failed to load booking details. Please try again."
          );
          setIsLoading(false);
        }
      }
    };

    fetchBookingData();
  }, [trace_id, result_idx, userToken, navigate]);

  const handleRetry = () => {
    // console.log("Retrying data fetch...");
    window.location.reload();
  };

  // State for payment data from create_order
  const [paymentData, setPaymentData] = useState(null);

  const handleProceedToPayment = (orderData) => {
    //console.log("[BookFlight] Proceeding to payment with order data:", orderData);
    setPaymentData(orderData);
    setCurrentPage("payment");
  };

  const handlePaymentSuccess = () => {
    setCurrentPage("confirmation");
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  useEffect(() => {
    const handleOpenContactSupport = () => {
      openContactModal();
    };

    window.addEventListener('open-contact-support', handleOpenContactSupport);

    return () => {
      window.removeEventListener('open-contact-support', handleOpenContactSupport);
    };
  }, []);

  // Fallback Modal Handlers
  const handleContinueWithMock = () => {
    //console.log("[BookFlight] User chose to continue with mock data");
    if (fallbackError && fallbackError.fallbackData) {
      // Determine which API failed and set the appropriate data
      if (fallbackError.operationName === "Fetch Fare Quote") {
        setFareQuoteData(fallbackError.fallbackData.data);
        // If we have fare quote but still need fare rules, fetch it
        if (!fareRulesData) {
          fetchFareRules(trace_id, result_idx, userToken)
            .then((response) => {
              setFareRulesData({
                ...response.data,
                rc_booking_id: response.rc_booking_id
              });
            })
            .catch((err) => {
              if (err instanceof FallbackAvailableError) {
                setFareRulesData({
                  ...err.fallbackData.data,
                  rc_booking_id: err.fallbackData.rc_booking_id
                });
              }
            });
        }
      } else if (fallbackError.operationName === "Fetch Fare Rules") {
        setFareRulesData({
          ...fallbackError.fallbackData.data,
          rc_booking_id: fallbackError.fallbackData.rc_booking_id
        });
        // If we have fare rules but still need fare quote, fetch it
        if (!fareQuoteData) {
          fetchFareQuote(trace_id, result_idx, userToken)
            .then((response) => {
              setFareQuoteData(response.data);
            })
            .catch((err) => {
              if (err instanceof FallbackAvailableError) {
                setFareQuoteData(err.fallbackData.data);
              }
            });
        }
      }
    }
    setFallbackError(null);
  };

  const handleTryAgainFallback = () => {
    //console.log("[BookFlight] User chose to try again");
    setFallbackError(null);
    setIsLoading(true);
    window.location.reload();
  };

  const handleGoBackFallback = () => {
    //console.log("[BookFlight] User chose to go back");
    setFallbackError(null);
    const sourcePage = location.state?.source || "chat";
    if (sourcePage === "search") {
      navigate("/");
    } else {
      navigate("/chat");
    }
  };

  // Intercept back button and refresh when transaction is active
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isTransactionActive) {
        e.preventDefault();
        e.returnValue = 'Booking is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (isTransactionActive) {
        const confirmLeave = window.confirm('A booking is in progress. If you go back, your current progress will be lost. Are you sure you want to cancel the booking process?');

        if (confirmLeave) {
          // If user confirms, we stop the transaction block. 
          // History already moved back to the 'real' URL when back was pressed.
          setIsTransactionActive(false);
        } else {
          // If they cancel leave, push the dummy state back to keep intercepting
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    if (isTransactionActive) {
      // Add a dummy state to history to enable catching the back button
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isTransactionActive, navigate]);


  return (
    <div className="min-h-screen bg-gray-50 mt-16">

      {/* Loading State */}
      <LoadingModal
        isOpen={isLoading}
        title="Loading Your Booking Details"
        message="This will only take a moment. Thank you for your patience."
        showWarning={true}
        warningText="Please do not refresh this page or use the back button while we load your booking details."
      />

      {/* Fallback Error Modal */}
      <FallbackErrorModal
        isOpen={!!fallbackError}
        operationName={fallbackError?.operationName || ""}
        errorMessage={fallbackError?.message || ""}
        errorDetails={fallbackError?.errorDetails}
        onContinueWithMock={handleContinueWithMock}
        onTryAgain={handleTryAgainFallback}
        onGoBack={handleGoBackFallback}
      />

      {/* Error State */}
      {!isLoading && apiError && (
        <BookingReviewError
          errorMessage={apiError}
          onRetry={handleRetry}
          onSignIn={
            apiError.includes("Authentication required")
              ? () => openLoginModal(true)
              : null
          }
        />
      )}

      {/* Success State - Show Booking UI */}
      {!isLoading && !apiError && fareQuoteData && fareRulesData && (
        <div className="max-w-[1440px] mx-auto p-4 sm:p-6 lg:p-8">
          {currentPage === "booking" ? (
            <div className="flex flex-col-reverse lg:flex-row gap-6">
              {/* Main Content */}
              <div className="flex-1 space-y-6 w-full lg:max-w-[calc(100%-384px)]">
                <BookingReview
                  onContactSupport={openContactModal}
                  fareQuoteData={fareQuoteData}
                  fareRulesData={fareRulesData}
                />

                <TravellerDetails
                  fareQuoteData={fareQuoteData}
                  trace_id={trace_id}
                  result_idx={result_idx}
                  contactDetails={contactDetails}
                  setContactDetails={setContactDetails}
                  passengers={passengers}
                  setPassengers={setPassengers}
                />
                <FareRules
                  fareRulesData={fareRulesData}
                  fareQuoteData={fareQuoteData}
                  onProceedToPayment={handleProceedToPayment}
                  trace_id={trace_id}
                  result_idx={result_idx}
                  userToken={userToken}
                  contactDetails={contactDetails}
                  passengers={passengers}
                  rc_booking_id={fareRulesData?.rc_booking_id}
                  amount={fareRulesData?.data?.Results?.Fare?.PublishedFare || fareQuoteData?.Results?.Fare?.PublishedFare || "5000"}
                  is_lcc={is_lcc === "true" || is_lcc === true}
                  isTransactionActive={isTransactionActive}
                  onTransactionStateChange={setIsTransactionActive}
                  source={location.state?.source || "chat"}
                />
              </div>

              {/* Sidebar */}
              <div className="w-full lg:w-[360px] flex-shrink-0">
                <FareDetails fareQuoteData={fareQuoteData} />
              </div>
            </div>
          ) : (
            <BookingConfirmation
              onContactSupport={openContactModal}
              fareQuoteData={fareQuoteData}
            />
          )}
        </div>
      )}

      {/* Contact Support Modal */}
      <ContactSupportModal
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
      />

      {/* recaptcha-container will be created by useAuthHandlers if needed */}
    </div>
  );
};

export default BookFlight;